import { useState, useEffect, useCallback } from 'react';
import appStateManager from '../lib/utils/appStateManager';

/**
 * Custom hook for accessing global application state
 * Provides reactive state and methods to fetch user details and subscription status
 */
const useAppState = () => {
  // User details state
  const [userDetails, setUserDetails] = useState(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [userDetailsError, setUserDetailsError] = useState(null);
  
  // Subscription status state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  
  /**
   * Fetch user details
   */
  const fetchUserDetails = useCallback(async (force = false) => {
    try {
      setUserDetailsLoading(true);
      setUserDetailsError(null);
      
      // If force is true, reset the cache first
      if (force) {
        appStateManager.resetCache();
      }
      
      const result = await appStateManager.getUserDetails();
      setUserDetails(result.data);
      return result;
    } catch (error) {
      setUserDetailsError(error);
      return { data: null, error };
    } finally {
      setUserDetailsLoading(false);
    }
  }, []);
  
  /**
   * Fetch subscription status
   */
  const fetchSubscriptionStatus = useCallback(async (force = false) => {
    try {
      setSubscriptionLoading(true);
      setSubscriptionError(null);
      
      // If force is true, reset the cache first
      if (force) {
        appStateManager.resetCache();
      }
      
      const result = await appStateManager.getSubscriptionStatus();
      setSubscriptionStatus(result);
      return result;
    } catch (error) {
      setSubscriptionError(error);
      return null;
    } finally {
      setSubscriptionLoading(false);
    }
  }, []);
  
  // Effect for loading user details on mount
  useEffect(() => {
    fetchUserDetails();
    
    // Set up a listener for user details changes
    const unsubscribe = appStateManager.subscribe('userDetails', (result) => {
      setUserDetails(result.data);
    });
    
    return unsubscribe;
  }, [fetchUserDetails]);
  
  // Effect for loading subscription status on mount
  useEffect(() => {
    fetchSubscriptionStatus();
    
    // Set up a listener for subscription status changes
    const unsubscribe = appStateManager.subscribe('subscriptionStatus', (result) => {
      setSubscriptionStatus(result);
    });
    
    return unsubscribe;
  }, [fetchSubscriptionStatus]);
  
  return {
    // User details
    userDetails,
    userDetailsLoading,
    userDetailsError,
    fetchUserDetails,
    
    // Subscription status
    subscriptionStatus,
    subscriptionLoading,
    subscriptionError,
    fetchSubscriptionStatus,
    
    // Helper getters
    isSubscriptionActive: subscriptionStatus?.currentValidSubscription || false,
    hasBillingAccount: subscriptionStatus?.billingAcctSetup || false
  };
};

export default useAppState; 